<template>
  <transition name="transient-notification">
    <div
      v-if="current"
      class="transient-notification"
      :class="kindClass"
      role="status"
      aria-live="polite"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @focusin="onFocusIn"
      @focusout="onFocusOut">
      <div class="transient-notification__body">
        <div class="transient-notification__icon">
          <i :class="iconClass" aria-hidden="true"></i>
        </div>
        <p class="transient-notification__message">{{ current.message }}</p>
        <button
          v-if="current.dismissible !== false"
          type="button"
          class="transient-notification__close"
          :aria-label="current.dismissLabel || 'Fechar notificação'"
          @click="dismiss">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>
      <div class="transient-notification__progress-track">
        <div
          class="transient-notification__progress-bar"
          :style="progressStyle"></div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState, mapActions } from 'vuex';

const KIND_TO_ICON = {
  success: 'fa fa-check-circle',
  error: 'fa fa-times-circle',
  info: 'fa fa-info-circle',
};

export default {
  name: 'TransientNotification',
  data() {
    return {
      tickIntervalId: null,
    };
  },
  computed: {
    ...mapState('notification', ['current']),

    /** The CSS modifier matching the kind; defaults to ``success``. */
    kindClass() {
      const kind = (this.current && this.current.kind) || 'success';
      return 'transient-notification--' + kind;
    },

    /** Font Awesome class for the leading icon. */
    iconClass() {
      const kind = (this.current && this.current.kind) || 'success';
      return KIND_TO_ICON[kind] || KIND_TO_ICON.success;
    },

    /**
     * Inline width binding for the progress bar. The store decrements
     * ``remainingMs`` on every tick; we render the ratio as a width.
     */
    progressStyle() {
      if (!this.current) {
        return { width: '0%' };
      }
      const { durationMs, remainingMs } = this.current;
      if (durationMs <= 0) {
        return { width: '0%' };
      }
      const ratio = Math.max(0, Math.min(1, remainingMs / durationMs));
      return { width: (ratio * 100).toFixed(2) + '%' };
    },
  },
  watch: {
    /**
     * Restart the tick loop whenever a new notification appears, and
     * cancel it when the notification is cleared. Pause state is
     * handled inside the tick itself rather than by tearing the
     * interval down so resume after hover does not skip a frame.
     */
    current: {
      immediate: true,
      handler(value) {
        if (!value) {
          this.stopTicking();
        } else {
          this.startTicking();
        }
      },
    },
  },
  beforeDestroy() {
    this.stopTicking();
  },
  methods: {
    ...mapActions('notification', ['dismiss', 'pause', 'resume', 'tick']),

    /** Begin a 100ms tick loop that advances ``remainingMs`` in the store. */
    startTicking() {
      this.stopTicking();
      this.tickIntervalId = setInterval(() => this.tick(100), 100);
    },

    /** Stop the tick loop without dismissing the current notification. */
    stopTicking() {
      if (this.tickIntervalId) {
        clearInterval(this.tickIntervalId);
        this.tickIntervalId = null;
      }
    },

    onMouseEnter() {
      this.pause();
    },
    onMouseLeave() {
      this.resume();
    },
    onFocusIn() {
      this.pause();
    },
    onFocusOut() {
      this.resume();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/_variables';

.transient-notification {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1080;
  width: calc(100vw - 3rem);
  max-width: 28rem;
  background: #ffffff;
  border: 1px solid rgba($theme-green, 0.45);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 10px rgba(0, 0, 0, 0.04);
  overflow: hidden;

  &--error {
    border-color: rgba($theme-magenta, 0.45);
  }
  &--info {
    border-color: rgba($theme-blue, 0.45);
  }
}

.transient-notification__body {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
}

.transient-notification__icon {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  background: rgba($theme-green, 0.14);
  color: darken($theme-green, 8%);

  .transient-notification--error & {
    background: rgba($theme-magenta, 0.14);
    color: darken($theme-magenta, 8%);
  }
  .transient-notification--info & {
    background: rgba($theme-blue, 0.14);
    color: darken($theme-blue, 8%);
  }
}

.transient-notification__message {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  line-height: 1.5;
  color: $gray-darker;
  margin: 6px 0;
}

.transient-notification__close {
  flex: 0 0 auto;
  appearance: none;
  background: transparent;
  border: 0;
  color: $gray-light;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover,
  &:focus {
    background: $gray-lighter;
    color: $gray-darker;
    outline: none;
  }
}

.transient-notification__progress-track {
  height: 3px;
  background: rgba($theme-green, 0.14);

  .transient-notification--error & {
    background: rgba($theme-magenta, 0.14);
  }
  .transient-notification--info & {
    background: rgba($theme-blue, 0.14);
  }
}

.transient-notification__progress-bar {
  height: 100%;
  background: $theme-green;
  transition: width 0.1s linear;

  .transient-notification--error & {
    background: $theme-magenta;
  }
  .transient-notification--info & {
    background: $theme-blue;
  }
}

// Slide-down + fade enter/leave.
.transient-notification-enter-active,
.transient-notification-leave-active {
  transition: opacity 0.18s ease-out, transform 0.18s ease-out;
}
.transient-notification-enter,
.transient-notification-leave-to {
  opacity: 0;
  transform: translateY(-16px);
}
</style>
